import { IconExternalLink, IconReceipt } from '@tabler/icons-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PaymentWithRelations } from '@/api/payments';
import { DetailInfoCell } from '@/components/detail-page-primitives';
import { COMPLETED_STATUS_STYLES } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  focusDialogOnOpen,
} from '@/components/ui/dialog';
import { formatAmount, formatCalendarDate, formatDate } from '@/lib/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  payment: PaymentWithRelations | null;
  /** Override for the external ref label (from party setting external_ref_display_name). */
  externalRefLabel?: string;
}

const getDirectionLabel = (direction: 'DEBIT' | 'CREDIT'): string =>
  direction === 'DEBIT' ? 'Outgoing' : 'Incoming';

const getDirectionVariant = (direction: 'DEBIT' | 'CREDIT'): 'default' | 'secondary' | 'outline' =>
  direction === 'DEBIT' ? 'secondary' : 'default';

const getStatusVariant = (
  statusCode: string
): 'default' | 'secondary' | 'outline' | 'completed' | 'returned' => {
  const code = statusCode.toUpperCase();
  if (code.includes('COMPLETE')) return COMPLETED_STATUS_STYLES.badge;
  if (code === 'RELEASED' || code === 'REISSUED') return 'outline';
  if (code.includes('SUCCESS') || code.includes('APPROVED')) return 'default';
  if (code.includes('PENDING') || code.includes('PROCESSING') || code.includes('AWAITING')) {
    return 'secondary';
  }
  if (code.includes('RETURN')) return 'returned';
  return 'outline';
};

/** Derive the payee display name from the payment relations (mirrors PaymentsTable). */
const getPayeeName = (payment: PaymentWithRelations): string => {
  const payeeParticipant = payment.instructions?.[0]?.participants?.find(
    (p) => p.participant_role === 'PAYEE'
  );
  return (payment.payee_party?.party_name ||
    payment.payee_name ||
    payeeParticipant?.party_snapshot_json?.display_name ||
    payeeParticipant?.party_snapshot_json?.legal_name ||
    '—') as string;
};

/**
 * Lightweight read-only quick view of a payment shown from the Payments table
 * quick-actions menu. Surfaces the headline details already present on the row,
 * with a button to open the full payment detail page.
 */
export const PaymentQuickViewModal: FC<Props> = ({
  open,
  onClose,
  payment,
  externalRefLabel = 'External Ref',
}) => {
  const navigate = useNavigate();

  if (!payment) return null;

  const title = payment.external_id ?? `${payment.id.slice(0, 8)}…`;

  const handleViewDetails = () => {
    onClose();
    navigate(`/payments/${payment.id}`);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl" onOpenAutoFocus={focusDialogOnOpen}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 pr-6">
            <IconReceipt className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="truncate">Payment {title}</span>
            <Badge variant={getStatusVariant(payment.status.code)}>{payment.status.name}</Badge>
          </DialogTitle>
          <DialogDescription>
            Created {formatDate(payment.created_at)}
            {payment.payment_method?.code ? ` · ${payment.payment_method.code}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <DetailInfoCell
              label="Amount"
              value={
                <span className="tabular-nums">
                  {formatAmount(payment.amount_total, payment.currency.code)}
                </span>
              }
            />
            <DetailInfoCell
              label="Direction"
              value={
                <Badge variant={getDirectionVariant(payment.direction)}>
                  {getDirectionLabel(payment.direction)}
                </Badge>
              }
            />
            <DetailInfoCell label="Payer" value={payment.payer_party.party_name || '—'} />
            <DetailInfoCell label="Payee" value={getPayeeName(payment)} />
            <DetailInfoCell label={externalRefLabel} value={payment.external_reference || '—'} />
            <DetailInfoCell label="Method" value={payment.payment_method?.code || '—'} />
            <DetailInfoCell label="Items" value={String(payment.item_count ?? 0)} />
            <DetailInfoCell
              label="Execution Date"
              value={
                payment.execution_date_requested
                  ? formatCalendarDate(payment.execution_date_requested)
                  : '—'
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleViewDetails}>
            View Full Details
            <IconExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
